import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import company from '../data/company';
import content from '../data/content';
import { mainNav } from '../data/navigation';
import BrandMark from './BrandMark';
import Button from './Button';
import './Header.css';

/**
 * Верхняя панель: фирменный знак, основная навигация и постоянный призыв к действию.
 * На узких экранах превращается в полноэкранное меню.
 */
function Header() {
  const copy = content.header;
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      <header className={`header${isScrolled ? ' is-scrolled' : ''}`}>
        <div className="container header__inner">
          <Link
            to="/"
            className="brand"
            aria-label={copy.brandHomeLabel(company.fullName)}
          >
            <BrandMark size={30} />
            <span className="brand__name" aria-hidden="true">
              {Array.from(company.name).map((letter, index) => (
                <span
                  className="brand__letter"
                  key={`${letter}-${index}`}
                  style={{ '--letter-index': index }}
                >
                  {letter}
                </span>
              ))}
            </span>
          </Link>

          <nav className="header__nav" aria-label={copy.mainNavigation}>
            {mainNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `header__link${isActive ? ' is-active' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="header__actions">
            <Button to="/#contact" size="sm">
              {copy.discussTask}
            </Button>
            <Button to="/projects" variant="ghost" size="sm">
              {copy.viewProjects}
            </Button>
            <button
              type="button"
              className={`burger${isOpen ? ' is-open' : ''}`}
              aria-label={isOpen ? copy.closeMenu : copy.openMenu}
              aria-expanded={isOpen}
              aria-controls="mobile-nav"
              onClick={() => setIsOpen((value) => !value)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {isOpen && (
        <div className="mobile-nav" id="mobile-nav">
          <nav aria-label={copy.mobileNavigation}>
            <ul className="mobile-nav__list">
              {mainNav.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `mobile-nav__link${isActive ? ' is-active' : ''}`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <Button to="/#contact" size="lg" className="mobile-nav__cta">
            {copy.discussTask}
          </Button>

          <div className="mobile-nav__meta">
            <a href={company.phoneHref}>{company.phone}</a>
            <a href={`mailto:${company.email}`}>{company.email}</a>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
